<template>
  <header class="header text-white py-4">
    <div class="container">
      <h1 class="display-5 mb-0">Grand Holidays</h1>
      <p class="subtitle mb-0">Who's hosting, and when</p>
    </div>
  </header>

  <main class="container my-4">
    <section v-if="upcoming.length" class="next-up mb-5">
      <h2 class="section-title">Next up</h2>
      <div class="row g-3">
        <div v-for="entry in upcoming" :key="entry.key + entry.year" class="col-12 col-md-6">
          <div class="card next-card h-100 shadow-sm" :class="`holiday--${entry.key}`">
            <div class="card-body">
              <p class="next-card__holiday mb-2">
                <span class="emoji">{{ entry.emoji }}</span>{{ entry.name }} {{ entry.year }}
              </p>
              <p class="next-card__label mb-0">Hosting</p>
              <p v-if="entry.host" class="next-card__host mb-2">{{ entry.host }}</p>
              <p v-else class="next-card__host next-card__host--unknown mb-2">Not settled yet</p>
              <p class="next-card__when mb-0">
                {{ formatHolidayDate(entry.date) }} &middot;
                <strong>{{ countdownLabel(entry.daysAway) }}</strong>
              </p>
              <button class="btn btn-sm btn-outline-dark mt-3" @click="addToCalendar(entry)">
                <i class="bi bi-calendar-plus me-1"></i>Add to calendar
              </button>
            </div>
          </div>
        </div>
      </div>
      <p class="subscribe mt-3 mb-0">
        <i class="bi bi-calendar-check me-1"></i>
        <a href="webcal://grand.family/holidays.ics">Subscribe to the whole schedule</a>
        and every year through {{ lastYear }} shows up in your calendar - no need to
        check back here.
        <span class="subscribe__fallback d-block">
          Adding it by hand instead? Use
          <a href="https://grand.family/holidays.ics">grand.family/holidays.ics</a>.
        </span>
      </p>
    </section>

    <h2 class="section-title">The whole rotation</h2>
    <section class="row g-3">
      <div v-for="holiday in holidays" :key="holiday.key" class="col-12 col-md-6">
        <div class="card schedule-card h-100 shadow-sm" :class="`holiday--${holiday.key}`">
          <div class="card-header">
            <h3 class="h5 mb-0"><span class="emoji">{{ holiday.emoji }}</span>{{ holiday.name }}</h3>
          </div>
          <ul class="list-group list-group-flush">
            <li
              v-for="entry in visibleYears(holiday.key)"
              :key="entry.year"
              class="list-group-item"
              :class="{ 'is-past': entry.isPast, 'is-next': isNext(entry) }"
            >
              <span class="year">{{ entry.year }}</span>
              <span class="host">{{ entry.host }}</span>
              <span v-if="isNext(entry)" class="badge next-badge ms-auto">next</span>
            </li>
          </ul>
          <div v-if="pastCount(holiday.key)" class="card-footer bg-white">
            <button class="btn btn-sm btn-link p-0" @click="togglePast(holiday.key)">
              {{ showPast[holiday.key] ? 'Hide' : 'Show' }}
              {{ pastCount(holiday.key) }} earlier {{ pastCount(holiday.key) === 1 ? 'year' : 'years' }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <p class="footnote mt-4 mb-0">
      Scheduled through {{ lastYear }}. <a href="https://grand.family">grand.family</a>
    </p>
  </main>
</template>

<script>
import {
  HOLIDAYS, upcomingHolidays, scheduleFor, formatHolidayDate, countdownLabel, lastScheduledYear
} from './utils/holidays'
import { downloadIcs } from './utils/calendar'

export default {
  name: 'GrandFamilyHolidays',
  data () {
    return {
      // Read the clock once, so every countdown on the page agrees with itself.
      today: new Date(),
      showPast: Object.fromEntries(HOLIDAYS.map((holiday) => [holiday.key, false]))
    }
  },
  computed: {
    holidays () {
      return HOLIDAYS
    },
    upcoming () {
      return upcomingHolidays(this.today)
    },
    lastYear () {
      return lastScheduledYear()
    },
    schedules () {
      return Object.fromEntries(
        HOLIDAYS.map((holiday) => [holiday.key, scheduleFor(holiday.key, this.today)])
      )
    }
  },
  methods: {
    formatHolidayDate,
    countdownLabel,
    visibleYears (holidayKey) {
      const years = this.schedules[holidayKey]
      return this.showPast[holidayKey] ? years : years.filter((entry) => !entry.isPast)
    },
    pastCount (holidayKey) {
      return this.schedules[holidayKey].filter((entry) => entry.isPast).length
    },
    // The soonest year still ahead of us for this holiday - the row worth
    // finding when you open the page in a hurry.
    isNext (entry) {
      const next = this.schedules[entry.key].find((year) => !year.isPast)
      return Boolean(next) && next.year === entry.year
    },
    togglePast (holidayKey) {
      this.showPast[holidayKey] = !this.showPast[holidayKey]
    },
    addToCalendar (entry) {
      downloadIcs(entry)
    }
  }
}
</script>

<style lang="scss">
body {
  background-color: #f6f7f9;
}
</style>

<style scoped lang="scss">
$thanksgiving: #b4560f;
$christmas: #16663a;

.header {
  background: linear-gradient(135deg, #0f69eb, #285391);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 0;

  h1 {
    font-weight: bold;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
    margin: 0;
  }

  .subtitle {
    opacity: 0.85;
  }
}

.container {
  max-width: 720px;
}

.section-title {
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.emoji {
  margin-right: 0.4rem;
}

.card {
  border: none;
  border-radius: 8px;
  overflow: hidden;
}

// Each holiday carries its own colour the whole way down the page, so the
// next-up card and its rotation card read as the same thing.
.holiday--thanksgiving {
  border-top: 4px solid $thanksgiving;

  .card-header { background-color: rgba($thanksgiving, 0.1); color: $thanksgiving; }
  .next-card__host { color: $thanksgiving; }
  .next-badge { background-color: $thanksgiving; }
  .is-next { background-color: rgba($thanksgiving, 0.07); }
}

.holiday--christmasEve {
  border-top: 4px solid $christmas;

  .card-header { background-color: rgba($christmas, 0.1); color: $christmas; }
  .next-card__host { color: $christmas; }
  .next-badge { background-color: $christmas; }
  .is-next { background-color: rgba($christmas, 0.07); }
}

.card-header {
  border-bottom: none;
  font-weight: 600;
}

.next-card {
  &__holiday {
    color: #6c757d;
    font-size: 0.95rem;
    font-weight: 600;
  }

  &__label {
    color: #adb5bd;
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__host {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.15;

    &--unknown {
      color: #adb5bd;
      font-size: 1.25rem;
      font-style: italic;
    }
  }

  &__when {
    color: #495057;
  }
}

.schedule-card {
  .list-group-item {
    align-items: baseline;
    background-color: transparent;
    display: flex;
    gap: 0.75rem;
    padding: 0.4rem 0.9rem;

    .year {
      color: #868e96;
      font-size: 0.8rem;
      font-variant-numeric: tabular-nums;
      font-weight: 700;
      min-width: 2.6rem;
    }

    .host {
      font-size: 1rem;
    }

    &.is-past {
      .year, .host { color: #adb5bd; }
    }

    &.is-next {
      .host { font-weight: 700; }
    }
  }

  .next-badge {
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .card-footer {
    border-top: none;
    padding-top: 0;
  }
}

.subscribe {
  color: #6c757d;
  font-size: 0.85rem;

  &__fallback {
    color: #adb5bd;
    font-size: 0.8rem;
    margin-top: 0.15rem;
  }
}

.footnote {
  color: #adb5bd;
  font-size: 0.8rem;
  text-align: center;
}
</style>
